import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Ticket, MessageCircle, Gift, Radar, Trophy, Home, Store } from "lucide-react";
import { ensureAnonUser } from "@/lib/anonSession";
import api from "@/lib/api";
import InstallPrompt from "@/components/hola/InstallPrompt";

const TABS = [
  { to: "/mi", icon: Home, label: "Inicio", key: "home" },
  { to: "/cuponera", icon: Ticket, label: "Cuponera", key: "cuponera" },
  { to: "/mi/boletos", icon: Gift, label: "Boletos", key: "boletos" },
  { to: "/mi/chat", icon: MessageCircle, label: "Chat", key: "chat" },
  { to: "/radar", icon: Radar, label: "Radar", key: "radar" },
];

/** Barra inferior del dashboard del usuario (PWA). */
export function UserTabBar({ active }) {
  const [unread, setUnread] = useState(0);
  useEffect(() => {
    const id = localStorage.getItem("holaradar_device_id");
    if (!id) return;
    api.get(`/pwa/chat/${id}`).then((r) => setUnread(r.data.chats.reduce((a, c) => a + (c.unread_user || 0), 0))).catch(() => {});
  }, [active]);
  return (
    <nav className="fixed bottom-0 inset-x-0 z-50 bg-[#0A0A0A]/95 backdrop-blur border-t border-white/10" data-testid="user-tabbar">
      <div className="max-w-md mx-auto grid grid-cols-5">
        {TABS.map((t) => (
          <Link key={t.key} to={t.to} className={`relative flex flex-col items-center gap-0.5 py-2.5 text-[10px] font-bold ${active === t.key ? "text-[#FF007A]" : "text-white/60"}`} data-testid={`user-tab-${t.key}`}>
            <t.icon className="w-5 h-5" /> {t.label}
            {t.key === "chat" && unread > 0 && <span className="absolute top-1.5 right-1/4 min-w-[16px] h-4 rounded-full bg-[#FF007A] text-white text-[9px] grid place-items-center px-1">{unread}</span>}
          </Link>
        ))}
      </div>
    </nav>
  );
}

/** Home del usuario: accesos a todos sus módulos. */
export default function UserHubPage() {
  const [name, setName] = useState("");
  const [data, setData] = useState({});
  const navigate = useNavigate();
  const { search } = useLocation();

  useEffect(() => {
    (async () => {
      const u = await ensureAnonUser();
      setName(u.display_name || "");
      const [c, t, ch, r] = await Promise.all([
        api.get(`/pwa/cuponera/${u.device_id}`).catch(() => ({ data: { summary: {} } })),
        api.get(`/pwa/raffle/${u.device_id}`).catch(() => ({ data: {} })),
        api.get(`/pwa/chat/${u.device_id}`).catch(() => ({ data: { chats: [] } })),
        api.get(`/pwa/gold/${u.device_id}`).catch(() => ({ data: null })),
      ]);
      setData({ cup: c.data.summary, raffle: t.data, chats: ch.data.chats, gold: r.data });
    })();
  }, []);

  const s = data.cup || {}; const rf = data.raffle || {};
  const money = (n) => `$${Number(n || 0).toLocaleString("es-MX", { maximumFractionDigits: 0 })}`;
  const fresh = new URLSearchParams(search).get("nuevo") === "1";

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white pb-24 relative overflow-x-hidden" data-testid="user-hub">
      <div className="absolute -top-32 right-0 w-80 h-80 rounded-full bg-[#FF007A]/25 blur-3xl pointer-events-none" />
      <div className="relative max-w-md mx-auto px-5 pt-8">
        <div className="text-white/60 text-sm">{name ? `Hola, ${name}` : "Hola"}</div>
        <h1 className="font-heading text-3xl font-black tracking-tight leading-none mt-1">Mi HOLA <span className="text-[#FF007A]">RADAR</span></h1>
        {fresh && <div className="mt-3 rounded-2xl bg-emerald-400/15 border border-emerald-400/30 text-emerald-200 text-xs font-bold p-3" data-testid="user-hub-welcome">¡Tu cuenta quedó lista! Aquí viven tus cupones, boletos del sorteo, chats y el radar. Guárdala en tu pantalla de inicio.</div>}

        <div className="mt-6 grid grid-cols-2 gap-3">
          <Tile to="/cuponera" icon={Ticket} title="Cuponera" big={s.available ?? 0} sub={`cupones · ahorro ${money(s.potential_savings)}`} color="#FF007A" testid="hub-cuponera" badge={s.new} />
          <Tile to="/mi/boletos" icon={Gift} title="Sorteo mensual" big={rf.my_tickets_month ?? 0} sub={rf.raffle ? `boletos · ${rf.raffle.title}` : "boletos"} color="#0055FF" testid="hub-boletos" />
          <Tile to="/mi/chat" icon={MessageCircle} title="Chat" big={(data.chats || []).length} sub="comercios y Aura" color="#10b981" testid="hub-chat" badge={(data.chats || []).reduce((a, c) => a + (c.unread_user || 0), 0)} />
          <Tile to="/radar" icon={Radar} title="Radar" big="GPS" sub="ofertas a 50 m" color="#f59e0b" testid="hub-radar" />
          <Tile to="/mi/tienda" icon={Store} title="Mi Tienda" big="$" sub="vende y gana comisión" color="#10b981" testid="hub-tienda" />
        </div>

        <button onClick={() => navigate("/mi/tienda")} className="mt-4 w-full text-left rounded-3xl p-4 text-[#07111f] flex items-center gap-3" style={{ background: "linear-gradient(135deg,#6ee7b7,#10b981 60%,#f7c948)" }} data-testid="hub-work-from-home">
          <Home className="w-8 h-8 shrink-0" /><div><div className="text-[10px] uppercase tracking-[0.2em] font-black">TRABAJA DESDE CASA</div><div className="font-heading font-extrabold leading-tight">Arma tu tienda con productos de los comercios y gana comisión por cada venta</div><div className="text-xs font-bold">Toca para ver cómo funciona →</div></div>
        </button>

        {data.gold?.prize && (
          <button onClick={() => navigate("/cuponera")} className="mt-4 w-full text-left rounded-3xl p-4 text-[#2b1a00] flex items-center gap-3" style={{ background: "linear-gradient(135deg,#fff3b0,#f7c948 50%,#d69e2e)" }} data-testid="hub-gold">
            <Trophy className="w-8 h-8" /><div><div className="text-[10px] uppercase tracking-widest font-black">Cupón de Oro del mes</div><div className="font-heading font-extrabold leading-tight">{data.gold.prize.title}</div><div className="text-xs font-bold">{data.gold.me?.rank ? `Vas en el lugar #${data.gold.me.rank}` : "Canjea cupones para participar"}</div></div>
          </button>
        )}

        <div className="mt-6"><InstallPrompt variant="compact" /></div>
      </div>
      <UserTabBar active="home" />
    </div>
  );
}

function Tile({ to, icon: Icon, title, big, sub, color, testid, badge }) {
  return (
    <Link to={to} className="relative rounded-3xl bg-white/5 border border-white/10 p-4 hover:bg-white/10 transition-colors" data-testid={testid}>
      <div className="w-10 h-10 rounded-xl grid place-items-center" style={{ background: color }}><Icon className="w-5 h-5 text-white" /></div>
      <div className="font-heading font-black text-3xl leading-none mt-3">{big}</div>
      <div className="font-bold text-sm mt-1">{title}</div>
      <div className="text-[11px] text-white/60 leading-tight">{sub}</div>
      {badge > 0 && <span className="absolute top-3 right-3 min-w-[20px] h-5 rounded-full bg-[#FF007A] text-[10px] font-black grid place-items-center px-1.5">{badge}</span>}
    </Link>
  );
}
